<template>
  <v-container>
    <v-row text-xs-center wrap>
      <v-col :cols="12">
        <v-card>
          <v-card-title>Email Log</v-card-title>
          <v-card-text>
            <span>This is a log of the emails that have been sent through Scots Connect and their status.</span>
            <v-container grid-list-md>
              <v-row row wrap>
                <v-col :cols="12" md="6" lg="4">
                  <v-select v-model="dateSearchSelect" :items="dateSearchOptions" label="Filter Run Date"></v-select>
                  <v-date-picker v-if="dateSearchSelect==='range'" v-model="dateFilterRange" range></v-date-picker>
                </v-col>
                <v-col :cols="12" md="6" lg="4">
                  <v-select v-model="statusFilter" :items="statusOptions" label="Status" multiple></v-select>
                </v-col>
                <v-col :cols="12" lg="4">
                  <v-text-field v-model="search" label="Filter Email (Subject/Body)" append-icon="fal fa-search"></v-text-field>
                </v-col>
              </v-row>
            </v-container>
            <v-data-table :items="logs" :headers="headers" :server-items-length="logCount" @update:options="loadLogs">
              <template v-slot:item.runDate="{ item }">
                {{ formatDate(item.runDate) }}
              </template>
              <template v-slot:item.body="{ item }">
                <div v-html="item.body" style='max-width:300px;max-height:80px;overflow:hidden;'></div>
              </template>
              <template v-slot:item.status="{ item }">
                <v-chip :color="alertColor(item.status)">{{ item.status }}</v-chip>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn icon :to="'/admin/tracker/email-log/'+item._id">
                  <v-icon>fal fa-eye</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { ref, watch, onMounted } from '@vue/composition-api'
export default {
  setup (props, { root }) {
    const search = ref('')
    const logs = ref([])
    const headers = ref([
      { text: 'Date', value: 'runDate' },
      { text: 'Subject', value: 'subject' },
      { text: 'Recipient Count', value: 'recipientCount' },
      { text: 'Status', value: 'status' },
      { text: 'User', value: 'username' },
      { text: 'Actions', value: 'actions' }
    ])
    const logCount = ref(0)
    const dateSearchSelect = ref('')
    const dateSearchOptions = ref([
      { text: 'Show All', value: '' },
      { text: 'Today', value: 'today' },
      { text: 'Past Week', value: 'week' },
      { text: 'Past Month', value: 'month' },
      { text: 'Select Range', value: 'range' }
    ])
    const dateFilterRange = ref([])
    const statusFilter = ref([])
    const statusOptions = ref([ 'Scheduled', 'Queued', 'In Progress', 'Complete', 'Cancelled' ])

    async function loadLogs (options) {
      let query = {
        $limit: 20,
        $skip: 0,
        $sort: {}
      }
      if (options) {
        const { sortBy, sortDesc, page, itemsPerPage } = options
        query.$limit = itemsPerPage
        query.$skip = (page - 1) * itemsPerPage
        for (let i = 0; i < sortBy.length; i++) {
          query.$sort[sortBy[i]] = sortDesc[i] ? -1 : 1
        }
      }
      let now = new Date()
      now.setHours(0)
      now.setMinutes(0)
      now.setSeconds(0)
      switch (dateSearchSelect.value) {
        case 'today':
          query.runDate = { $gt: now }
          break
        case 'week':
          now.setDate(now.getDate() - 7)
          query.runDate = { $gt: now }
          break
        case 'month':
          now.setMonth(now.getMonth() - 1)
          query.runDate = { $gt: now }
          break
        case 'range':
          if (Array.isArray(dateFilterRange.value) && dateFilterRange.value.length === 2) {
            let first = new Date(dateFilterRange.value[0])
            let second = new Date(dateFilterRange.value[1])
            if (dateFilterRange.value[0] > dateFilterRange.value[1]) {
              first = new Date(dateFilterRange.value[1])
              second = new Date(dateFilterRange.value[0])
            }
            first.setHours(0)
            first.setMinutes(0)
            first.setSeconds(0)
            second.setHours(23)
            second.setMinutes(59)
            second.setSeconds(59)
            query.runDate = { $gt: first, $lt: second }
          }
          break
      }
      if (statusFilter.value.length > 0) query.status = { $in: statusFilter.value }
      if (search.value !== '') {
        query.$or = [
          { subject: { $regex: search.value, $options: 'i' } },
          { body: { $regex: search.value, $options: 'i' } }
        ]
      }
      let { total, data } = await root.$feathers.service('orientation/email-log').find({ query })
      logCount.value = total
      logs.value = data
    }

    function alertColor (status) {
      switch (status) {
        case 'Scheduled':
        case 'Queued':
          return 'info'
        case 'In Progress':
          return 'warning'
        case 'Complete':
          return 'success'
        case 'Cancelled':
          return 'error'
      }
    }
    function formatDate (runDate) {
      let dt = new Date(runDate)
      let [ month, day, year ] = dt.toLocaleDateString().split('/')
      let temp = dt.toLocaleTimeString().split('')
      temp.splice(-6, 3)
      return year + '-' + (month < 10 ? '0' : '') + month + '-' + (day < 10 ? '0' : '') + day + ' ' + temp.join('')
    }

    watch([search, dateSearchSelect, dateFilterRange, statusFilter], () => loadLogs())

    onMounted(() => {
      if (!root.$store.getters.hasRole('Employee')) {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 6000, text: 'You do not have access to the admin system.' })
        root.$router.push('/')
      } else if (!root.$store.getters.isAdmin) {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 6000, text: 'You do not have access to the email tracker.' })
        root.$router.push('/admin')
      }
    })

    return {
      search,
      logs,
      headers,
      logCount,
      dateSearchSelect,
      dateSearchOptions,
      dateFilterRange,
      statusFilter,
      statusOptions,
      loadLogs,
      alertColor,
      formatDate
    }
  }
}
</script>
